import { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Plus,
  Search,
  FolderKanban,
  MoreVertical,
  Calendar,
  FileText,
  Trash2,
  Edit3,
  X,
  AlertCircle,
} from 'lucide-react'
import * as api from '../services/api'
import type { Project, CreateProjectRequest } from '../types'

function getStatusBadge(status: string) {
  switch (status) {
    case 'active':
      return <span className="badge-green">Active</span>
    case 'completed':
      return <span className="badge-blue">Completed</span>
    case 'on_hold':
      return <span className="badge-yellow">On Hold</span>
    case 'archived':
      return <span className="badge-gray">Archived</span>
    default:
      return <span className="badge-gray">{status}</span>
  }
}

function NewProjectModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState<CreateProjectRequest>({
    name: '',
    customer_name: '',
    description: '',
    salesforce_url: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim() || !formData.customer_name.trim()) return

    setLoading(true)
    setError(null)
    try {
      await api.createProject(formData)
      onCreated()
      onClose()
    } catch (err) {
      console.error('Failed to create project:', err)
      setError('Failed to create project. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal animate-scaleIn">
        <div className="modal-header">
          <h2 className="text-lg font-semibold text-slate-100">Create New Project</h2>
          <button onClick={onClose} className="btn-ghost btn-icon btn-sm" disabled={loading}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            {error && (
              <div className="alert-error">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}
            <div>
              <label className="label">Customer Name *</label>
              <input
                type="text"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                placeholder="e.g., Acme Corporation"
                className="input"
                required
                autoFocus
                disabled={loading}
              />
            </div>
            <div>
              <label className="label">Project Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Azure Migration Phase 2"
                className="input"
                required
                disabled={loading}
              />
            </div>
            <div>
              <label className="label">Salesforce URL</label>
              <input
                type="url"
                value={formData.salesforce_url || ''}
                onChange={(e) => setFormData({ ...formData, salesforce_url: e.target.value })}
                placeholder="https://coretek.my.salesforce.com/..."
                className="input"
                disabled={loading}
              />
            </div>
            <div>
              <label className="label">Description</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief project description..."
                className="textarea"
                rows={3}
                disabled={loading}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={loading}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !formData.name.trim() || !formData.customer_name.trim()}
            >
              {loading ? (
                <>
                  <div className="spinner" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Create Project
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function Projects() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showNewProject, setShowNewProject] = useState(searchParams.get('new') === 'true')
  const [menuOpen, setMenuOpen] = useState<string | null>(null)

  const fetchProjects = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.getProjects()
      setProjects(response.data.projects || [])
    } catch (err) {
      console.error('Failed to fetch projects:', err)
      setError('Failed to load projects')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      setShowNewProject(true)
      setSearchParams({})
    }
  }, [searchParams, setSearchParams])

  const handleDelete = async (projectId: string) => {
    if (!confirm('Are you sure you want to delete this project? This cannot be undone.')) return

    try {
      await api.deleteProject(projectId)
      await fetchProjects()
    } catch (err) {
      console.error('Failed to delete project:', err)
      setError('Failed to delete project')
    }
    setMenuOpen(null)
  }

  const filteredProjects = projects.filter(
    (project) =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.customer_name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Projects</h1>
          <p className="mt-1 text-slate-400">Manage your PMO projects and deliverables</p>
        </div>
        <button onClick={() => setShowNewProject(true)} className="btn-primary">
          <Plus className="w-4 h-4" />
          New Project
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="alert-error">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
        <input
          type="text"
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input pl-10"
        />
      </div>

      {/* Projects grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card">
              <div className="flex items-start justify-between mb-4">
                <div className="skeleton w-10 h-10 rounded-lg" />
              </div>
              <div className="skeleton h-5 w-3/4 mb-2" />
              <div className="skeleton h-4 w-1/2" />
            </div>
          ))}
        </div>
      ) : filteredProjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <div key={project.id} className="relative">
              <Link to={`/projects/${project.id}`} className="card glass-panel-hover block">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <FolderKanban className="w-5 h-5 text-blue-400" />
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setMenuOpen(menuOpen === project.id ? null : project.id)
                    }}
                    className="btn-ghost btn-icon btn-sm"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </div>

                <h3 className="font-semibold text-slate-100 group-hover:text-blue-400 transition-colors">
                  {project.name}
                </h3>
                <p className="text-sm text-slate-400 mt-1">{project.customer_name}</p>

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700/50">
                  <div className="flex items-center text-sm text-slate-500">
                    <FileText className="w-4 h-4 mr-1" />
                    {project.document_count || 0} docs
                  </div>
                  <div className="flex items-center text-sm text-slate-500">
                    <Calendar className="w-4 h-4 mr-1" />
                    {new Date(project.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="mt-3">{getStatusBadge(project.status)}</div>
              </Link>

              {/* Dropdown Menu */}
              {menuOpen === project.id && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(null)} />
                  <div className="dropdown top-14 right-2 animate-fadeIn z-50">
                    <Link
                      to={`/projects/${project.id}`}
                      className="dropdown-item flex items-center gap-2"
                      onClick={() => setMenuOpen(null)}
                    >
                      <Edit3 className="w-4 h-4" />
                      Edit Project
                    </Link>
                    <button
                      onClick={() => handleDelete(project.id)}
                      className="dropdown-item flex items-center gap-2 text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Project
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <FolderKanban className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-300 mb-2">No projects found</h3>
          <p className="text-slate-500 mb-4">
            {searchQuery
              ? 'Try adjusting your search terms'
              : 'Get started by creating your first project'}
          </p>
          {!searchQuery && (
            <button onClick={() => setShowNewProject(true)} className="btn-primary">
              <Plus className="w-4 h-4" />
              New Project
            </button>
          )}
        </div>
      )}

      {/* New Project Modal */}
      {showNewProject && (
        <NewProjectModal onClose={() => setShowNewProject(false)} onCreated={fetchProjects} />
      )}
    </div>
  )
}
